import { useEffect, useState, type FormEvent } from 'react';
import type {
  CreateVendorRequest,
  EntityKind,
  OrganizationContact,
  UpdateVendorRequest,
  Vendor,
} from '@stfors/shared-types';
import { Modal } from './Modal';
import { IconAction } from './IconAction';
import { PhoneInput } from './PhoneInput';
import { AddressFields } from './AddressFields';
import { ApiError } from '../api/client';
import { useCreateVendor, useUpdateVendor } from '../api/queries/vendors';
import { useOrganization, useAddOrgContact, useRemoveOrgContact } from '../api/queries/organizations';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import {
  addressValueFromColumns,
  addressValueToColumns,
  emptyAddressValue,
  type AddressValue,
} from '../utils/address';
import { stripPhoneForStorage, formatPhoneAsTyped } from '../utils/phone';
import { parseValidationError } from '../utils/apiErrors';
import { normalizeWebsite } from '../utils/website';
import { WebsiteInput } from './WebsiteInput';
import './VendorFormModal.css';

// The keys this form has an inline `<span className="field-error">` for.
// Address, dba_name, website and notes are deliberately absent: the API can
// reject them, and the banner is where those messages have to land.
const INLINE_FIELDS = ['name', 'last_name', 'phone', 'email', 'ein'] as const;

interface VendorFormModalProps {
  open: boolean;
  /** When set, edit this vendor; otherwise create a new one. */
  initial?: Vendor | null;
  onClose: () => void;
  /** Fires after a successful create/update with the persisted row. */
  onSaved?: (vendor: Vendor) => void;
}

function formatEin(raw: string): string {
  const d = raw.replace(/\D+/g, '').slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
}

interface FormState {
  // Individual identity
  first_name: string;
  last_name: string;
  // Company identity
  name: string;
  dba_name: string;
  // Address is held as structured AddressValue (addr state below) so mid-edit
  // keystrokes are not round-tripped through composeStreet/parseStreet — that
  // path trims spaces and mis-routes a lone house number into street name.
  email: string;
  website: string;
  ein: string;       // formatted XX-XXXXXXX; stripped on submit
  notes: string;
  // Shared
  phone: string;     // formatted display value; stripped on submit
  default_tax_category: string;
}

function blankForm(): FormState {
  return {
    first_name: '', last_name: '',
    name: '', dba_name: '',
    email: '', website: '', ein: '', notes: '',
    phone: '', default_tax_category: '',
  };
}

function formFromVendor(v: Vendor): FormState {
  return {
    first_name: v.first_name ?? '',
    last_name: v.last_name ?? '',
    // Company form "name" is the legal entity; API `name` is DBA-preferred display.
    name: v.kind === 'company' ? (v.legal_name ?? v.name) : v.name,
    dba_name: v.dba_name ?? '',
    email: v.email ?? '',
    website: v.website ?? '',
    ein: v.ein ? formatEin(v.ein) : '',
    notes: v.notes ?? '',
    phone: v.phone ? formatPhoneAsTyped(v.phone) : '',
    default_tax_category: v.default_tax_category ?? '',
  };
}

export function VendorFormModal({ open, initial, onClose, onSaved }: VendorFormModalProps): JSX.Element {
  const isEdit = !!initial;
  const create = useCreateVendor();
  const update = useUpdateVendor();
  const lookups = useLookups('tax_category');
  const taxCategoryOptions = activeOptionsFor(lookups.data?.lookup_options, 'tax_category');

  // kind is fixed once a vendor exists (switching identity would orphan the
  // person/org). New vendors default to company.
  const [kind, setKind] = useState<EntityKind>('company');
  const [form, setForm] = useState<FormState>(blankForm);
  const [addr, setAddr] = useState<AddressValue>(emptyAddressValue);
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setKind(initial?.kind ?? 'company');
      setForm(initial ? formFromVendor(initial) : blankForm());
      setAddr(initial ? addressValueFromColumns(initial) : emptyAddressValue());
      setTopError(null);
      setFieldErrors({});
    }
  }, [open, initial]);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) => {
    setForm((s) => ({ ...s, [key]: val }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    const phoneCanonical = stripPhoneForStorage(form.phone);
    if (form.phone.trim().length > 0 && !phoneCanonical) {
      setFieldErrors({ phone: 'Phone must contain 10 US digits' });
      return;
    }

    // Create and update payloads are built SEPARATELY and are not
    // interchangeable, for two reasons:
    //
    //   - PATCH's schema is `.strict()` and has no `kind` — a vendor's identity
    //     is fixed once the row exists. Spreading the create payload into the
    //     update sent `kind` and 400'd every single edit. TypeScript can't
    //     catch that: excess-property checking doesn't apply to spread
    //     properties, so `{ id, ...payload }` compiles clean.
    //   - POST omits blank fields (its schema is non-nullable `.optional()`, so
    //     a null would be rejected), while PATCH must send `null` for them —
    //     omitting a key there means "leave it alone", which made clearing a
    //     field on an edit silently do nothing.
    let createPayload: CreateVendorRequest;
    let updatePatch: UpdateVendorRequest;

    if (kind === 'individual') {
      if (form.last_name.trim().length === 0) {
        setFieldErrors({ last_name: 'Last name is required' });
        return;
      }
      createPayload = {
        kind: 'individual',
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim(),
        ...(phoneCanonical ? { phone: phoneCanonical } : {}),
        ...(form.default_tax_category ? { default_tax_category: form.default_tax_category } : {}),
      };
      updatePatch = {
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim(),
        phone: phoneCanonical,
        default_tax_category: form.default_tax_category || null,
      };
    } else {
      if (form.name.trim().length === 0) {
        setFieldErrors({ name: 'Vendor name is required' });
        return;
      }
      const einDigits = form.ein.replace(/\D+/g, '');
      if (form.ein.trim().length > 0 && einDigits.length !== 9) {
        setFieldErrors({ ein: 'EIN must be 9 digits (XX-XXXXXXX)' });
        return;
      }
      const cols = addressValueToColumns(addr);
      createPayload = {
        kind: 'company',
        name: form.name.trim(),
        ...(form.dba_name.trim() ? { dba_name: form.dba_name.trim() } : {}),
        ...(cols.line1 ? { address_line1: cols.line1 } : {}),
        ...(cols.line2 ? { address_line2: cols.line2 } : {}),
        ...(cols.city ? { city: cols.city } : {}),
        ...(cols.state ? { state: cols.state } : {}),
        ...(cols.postal_code ? { postal_code: cols.postal_code } : {}),
        ...(phoneCanonical ? { phone: phoneCanonical } : {}),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(normalizeWebsite(form.website) ? { website: normalizeWebsite(form.website) } : {}),
        ...(einDigits.length === 9 ? { ein: einDigits } : {}),
        ...(form.default_tax_category ? { default_tax_category: form.default_tax_category } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      };
      // `country` is intentionally absent: the form has no country control, so
      // sending one would overwrite the organization's stored value (which
      // defaults to 'US') with whatever a geocode happened to return.
      updatePatch = {
        name: form.name.trim(),
        dba_name: form.dba_name.trim() || null,
        address_line1: cols.line1,
        address_line2: cols.line2,
        city: cols.city,
        state: cols.state,
        postal_code: cols.postal_code,
        phone: phoneCanonical,
        email: form.email.trim() || null,
        website: normalizeWebsite(form.website) || null,
        ein: einDigits.length === 9 ? einDigits : null,
        default_tax_category: form.default_tax_category || null,
        notes: form.notes.trim() || null,
      };
    }

    try {
      const resp = isEdit && initial
        ? await update.mutateAsync({ id: initial.id, ...updatePatch })
        : await create.mutateAsync(createPayload);
      onSaved?.(resp.vendor);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setFieldErrors({ name: 'A company with that name already exists' });
        return;
      }
      const failure = parseValidationError(err, INLINE_FIELDS);
      if (failure) {
        setFieldErrors(failure.fieldErrors);
        setTopError(failure.message);
        return;
      }
      setTopError(`Could not ${isEdit ? 'save' : 'create'} the vendor. Please try again.`);
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Modal
      title={isEdit ? `Edit Vendor — ${initial?.name}` : 'Add Vendor'}
      open={open}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <IconAction action="cancel" onClick={onClose} disabled={busy} />
          {isEdit ? (
            <IconAction
              action="save"
              type="submit"
              form="vendor-form"
              disabled={busy}
              label={busy ? 'Saving…' : 'Save'}
            />
          ) : (
            <button type="submit" form="vendor-form" className="btn-cyan-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Add Vendor'}
            </button>
          )}
        </>
      }
    >
      <form id="vendor-form" className="vfm-form" onSubmit={onSubmit} noValidate>
        {/* Kind toggle — only switchable on create (an existing vendor's
            identity is fixed). */}
        <div className="vfm-row">
          <button
            type="button"
            className={kind === 'company' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => setKind('company')}
            disabled={isEdit || busy}
          >
            Company
          </button>
          <button
            type="button"
            className={kind === 'individual' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => setKind('individual')}
            disabled={isEdit || busy}
          >
            Individual
          </button>
        </div>

        {kind === 'individual' ? (
          <>
            <div className="vfm-row">
              <label className="vfm-field vfm-field-grow">
                <span>First name</span>
                <input
                  type="text"
                  className="form-input"
                  value={form.first_name}
                  onChange={(e) => set('first_name', e.target.value)}
                  maxLength={100}
                  autoComplete="given-name"
                />
              </label>
              <label className="vfm-field vfm-field-grow">
                <span>Last name *</span>
                <input
                  type="text"
                  className="form-input"
                  value={form.last_name}
                  onChange={(e) => set('last_name', e.target.value)}
                  maxLength={100}
                  required
                  autoComplete="family-name"
                />
                {fieldErrors['last_name'] && <span className="field-error">{fieldErrors['last_name']}</span>}
              </label>
            </div>
            <label className="vfm-field">
              <span>Phone</span>
              <PhoneInput value={form.phone} onChange={(v) => set('phone', v)} />
              {fieldErrors['phone'] && <span className="field-error">{fieldErrors['phone']}</span>}
            </label>
          </>
        ) : (
          <>
            <div className="vfm-row">
              <label className="vfm-field vfm-field-grow">
                <span>Vendor name *</span>
                <input
                  type="text"
                  className="form-input"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Acme Forensics LLC"
                  autoFocus
                />
                {fieldErrors['name'] && <span className="field-error">{fieldErrors['name']}</span>}
              </label>
              <label className="vfm-field vfm-field-grow">
                <span>Doing business as</span>
                <input
                  type="text"
                  className="form-input"
                  value={form.dba_name}
                  onChange={(e) => set('dba_name', e.target.value)}
                  maxLength={200}
                  placeholder="(optional)"
                />
              </label>
            </div>

            <fieldset className="vfm-fieldset">
              <legend>Address</legend>
              <AddressFields
                value={addr}
                onChange={setAddr}
                idPrefix="Vendor address"
              />
            </fieldset>

            <fieldset className="vfm-fieldset">
              <legend>Contact</legend>
              <div className="vfm-row">
                <label className="vfm-field vfm-field-grow">
                  <span>Phone</span>
                  <PhoneInput value={form.phone} onChange={(v) => set('phone', v)} />
                  {fieldErrors['phone'] && <span className="field-error">{fieldErrors['phone']}</span>}
                </label>
                <label className="vfm-field vfm-field-grow">
                  <span>Email</span>
                  <input type="email" className="form-input" value={form.email} onChange={(e) => set('email', e.target.value)} maxLength={200} autoComplete="email" />
                  {fieldErrors['email'] && <span className="field-error">{fieldErrors['email']}</span>}
                </label>
              </div>
              <label className="vfm-field">
                <span>Website</span>
                <WebsiteInput
                  value={form.website}
                  onChange={(v) => set('website', v)}
                  placeholder="acmeforensics.com"
                />
              </label>
            </fieldset>

            <fieldset className="vfm-fieldset">
              <legend>Tax info</legend>
              <div className="vfm-row">
                <label className="vfm-field vfm-field-grow">
                  <span>EIN</span>
                  <input type="text" className="form-input" value={form.ein} onChange={(e) => set('ein', formatEin(e.target.value))} placeholder="XX-XXXXXXX" maxLength={10} inputMode="numeric" />
                  {fieldErrors['ein'] && <span className="field-error">{fieldErrors['ein']}</span>}
                </label>
              </div>
            </fieldset>

            <label className="vfm-field">
              <span>Notes</span>
              <textarea className="form-input vfm-textarea" value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} maxLength={10_000} placeholder="Account number, payment preference, etc." />
            </label>
          </>
        )}

        {/* Default tax category applies to both kinds. */}
        <label className="vfm-field">
          <span>Default tax category</span>
          <select className="form-input" value={form.default_tax_category} onChange={(e) => set('default_tax_category', e.target.value)}>
            <option value="">— (none) —</option>
            {taxCategoryOptions.map((o) => (<option key={o.id} value={o.value}>{o.value}</option>))}
            {form.default_tax_category && !taxCategoryOptions.some((o) => o.value === form.default_tax_category) && (
              <option value={form.default_tax_category}>{form.default_tax_category} (legacy)</option>
            )}
          </select>
        </label>

        {topError && <p className="vfm-error-banner" role="alert">{topError}</p>}
      </form>

      {/* Contacts — only for an existing company vendor (its org holds them). */}
      {isEdit && initial?.kind === 'company' && initial.organization_id !== null && (
        <VendorContacts orgId={initial.organization_id} />
      )}
    </Modal>
  );
}

// ---------- Contacts on a company vendor ----------

function VendorContacts({ orgId }: { orgId: number }): JSX.Element {
  const { data, isPending } = useOrganization(orgId);
  const addContact = useAddOrgContact(orgId);
  const removeContact = useRemoveOrgContact(orgId);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const onAdd = async () => {
    setErr(null);
    if (last.trim().length === 0) {
      setErr('Last name is required.');
      return;
    }
    try {
      await addContact.mutateAsync({ first_name: first.trim() || undefined, last_name: last.trim() });
      setFirst('');
      setLast('');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not add the contact.');
    }
  };

  const contacts = (data?.organization.contacts ?? []) as OrganizationContact[];

  return (
    <div className="vfm-contacts">
      <h4 className="vfm-contacts-title">Contacts</h4>
      {isPending ? (
        <p className="vfm-contacts-empty">Loading…</p>
      ) : contacts.length === 0 ? (
        <p className="vfm-contacts-empty">No individuals attached yet.</p>
      ) : (
        <ul className="vfm-contacts-list">
          {contacts.map((c) => (
            <li key={c.id} className="vfm-contact-row">
              <span>
                {c.display_name ?? c.person?.display_name ?? 'Contact'}
                {c.is_primary && <span className="vfm-contact-primary">Primary</span>}
                {c.title && <span className="vfm-contact-title"> · {c.title}</span>}
              </span>
              <button
                type="button"
                className="btn-text"
                onClick={() => removeContact.mutate(c.id)}
                disabled={removeContact.isPending}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="vfm-row vfm-contacts-add">
        <input type="text" className="form-input" placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} maxLength={100} />
        <input type="text" className="form-input" placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)} maxLength={100} />
        <button type="button" className="btn-charcoal-secondary" onClick={onAdd} disabled={addContact.isPending || last.trim().length === 0}>
          {addContact.isPending ? 'Adding…' : 'Add contact'}
        </button>
      </div>
      {err && <p className="field-error">{err}</p>}
    </div>
  );
}
